import axios from "axios";
import {
  bondingLabel,
  customLabels,
  feeSimpleLabel,
  fieldMapping,
  fieldsToCheckNoFax,
  mortgageLabel,
  projectLabel,
} from "../../../data/buildingPermit/fieldDatas";
import {
  fieldsToCheckBonding,
  fieldsToCheckFeeSimple,
  fieldsToCheckMortgage,
  fieldsToCheckProject,
} from "../../../data/permitFormsData";
import { loadInitials } from "../../../utils/loadPermitInitials";
import { updatePermitPackage } from "../dataActions";
import { updateAnalytics, updateSubAnalytics } from "../subUserActions";
import { setDocumentImage } from "../uiActions/myPermitsActions";
import { getPermitTypeData } from "../../../components/permitForms/permitFormHelpers";

export const setMagnifierOn = (value) => {
  return {
    type: "SET_MAGNIFIER_ON",
    payload: value,
  };
};

export const setBuildingState = (payload) => {
  return {
    type: "SET_BUILDING_STATE",
    payload,
  };
};

export const setBuildingListState = (payload) => {
  return {
    type: "SET_BUILDING_LIST_STATES",
    payload,
  };
};

export const setBuildingListStateFull = (payload) => {
  return {
    type: "SET_BUILDING_LIST_STATES_FULL",
    payload,
  };
};

export const getFormInitials = () => async (dispatch, getState) => {
  const state = getState();
  const { selectedRow, leftValue, selectedPermitType } =
    state.myPermitsComponent;

  const currentPermitData = getPermitTypeData(selectedRow, selectedPermitType);

  let creatingDoc = currentPermitData?.selected_documents?.[leftValue]?.name;

  // For energy form, we need to explicitly set creatingDoc to "energy"
  // since it's a global form that doesn't depend on the selected document
  if (creatingDoc === "energy" || selectedPermitType === "energy") {
    creatingDoc = "energy";
  }

  const Initials = await loadInitials(
    creatingDoc,
    selectedRow?.county,
    selectedRow?.city
  );
  await dispatch(setBuildingState({ initials: Initials }));

  return true;
};

export const validateSavedProjects = (values) => async (dispatch, getState) => {
  const state = getState();
  const {
    initials,
    designProListState,
    projectContactListState,
    feeSimpleListState,
    bondingCompanyListState,
    mortgageLenderListState,
  } = state.buildingState;

  if (!initials?.[0]?.values) {
    return {};
  }

  const errors = {};

  if (values.designProfessional) {
    fieldsToCheckNoFax.forEach((field) => {
      const formikValue = values[field]?.trim().toLowerCase();
      const isDuplicate = designProListState.some((pro) => {
        const proValue = pro[fieldMapping[field]]?.trim().toLowerCase();
        return proValue === formikValue && pro.id !== values.designProfessional;
      });

      if (isDuplicate) {
        errors[field] = `${customLabels[field]} already exists`;
      }
    });
  }

  if (values.projectContact) {
    fieldsToCheckProject.forEach((field) => {
      const formikValue = values[field]?.trim().toLowerCase();
      const isDuplicate = projectContactListState.some((contact) => {
        const contactValue = contact[field]?.trim().toLowerCase();
        return (
          contactValue === formikValue && contact.id !== values.projectContact
        );
      });

      if (isDuplicate) {
        errors[field] = `${projectLabel[field]} already exists`;
      }
    });
  }

  if (values.feeSimpleTitleholder) {
    fieldsToCheckFeeSimple.forEach((field) => {
      const formikValue = values[field]?.trim().toLowerCase();
      const isDuplicate = feeSimpleListState.some((feeSimple) => {
        const feeSimpleValue = feeSimple[field]?.trim().toLowerCase();
        return (
          feeSimpleValue === formikValue &&
          feeSimple.id !== values.feeSimpleTitleholder
        );
      });

      if (isDuplicate) {
        errors[field] = `${feeSimpleLabel[field]} already exists`;
      }
    });
  }

  if (values.bondingCompany) {
    fieldsToCheckBonding.forEach((field) => {
      const formikValue = values[field]?.trim().toLowerCase();
      const isDuplicate = bondingCompanyListState.some((bondingCompany) => {
        const bondingCompanyValue = bondingCompany[field]?.trim().toLowerCase();
        return (
          bondingCompanyValue === formikValue &&
          bondingCompany.id !== values.bondingCompany
        );
      });

      if (isDuplicate) {
        errors[field] = `${bondingLabel[field]} already exists`;
      }
    });
  }

  if (values.mortgageLender) {
    fieldsToCheckMortgage.forEach((field) => {
      const formikValue = values[field]?.trim().toLowerCase();
      const isDuplicate = mortgageLenderListState.some((mortgageLender) => {
        const mortgageLenderValue = mortgageLender[field]?.trim().toLowerCase();
        return (
          mortgageLenderValue === formikValue &&
          mortgageLender.id !== values.mortgageLender
        );
      });

      if (isDuplicate) {
        errors[field] = `${mortgageLabel[field]} already exists`;
      }
    });
  }

  return errors;
};

export const createBuildingPermit =
  (values, updateCityData) => async (dispatch, getState) => {
    const state = getState();
    const currentSession = state.sessions.currentSession;
    const currentUser = state[currentSession]?.user;
    const { selectedRow, leftValue, selectedPermitType } =
      state.myPermitsComponent;
    const { pdfSize } = state.buildingState;

    try {
      await dispatch(setBuildingState({ createDocLoading: true }));

      // Resolve permit-type specific data
      const currentPermitData = getPermitTypeData(
        selectedRow,
        selectedPermitType
      );

      // Choose contractor based on permit type
      const contractorForPermit =
        selectedPermitType === selectedRow?.permit_type
          ? selectedRow?.contractor
          : selectedRow?.additional_permits?.[selectedPermitType]?.contractor ||
            selectedRow?.contractor;

      const payloadValues = {
        ...values,
        task_id: selectedRow?.task_id,
        county: selectedRow?.county,
      };

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/create-building-permit`,
        {
          values: payloadValues,
          form: currentPermitData?.selected_documents?.[leftValue],
          designProList: currentUser?.design_professionals,
          currentSession: currentSession,
          contractor: contractorForPermit,
          updateCityData: updateCityData,
          pdfSize: pdfSize,
          selectedPermitType: selectedPermitType,
          mainPermitType: selectedPermitType === selectedRow?.permit_type,
        },
        { withCredentials: true }
      );
      if (resp?.data?.success) {
        await dispatch(setDocumentImage(resp?.data?.signed));
        await dispatch(updatePermitPackage(resp?.data?.data));

        const isSub = resp?.data?.is_sub;
        const freshAnalytics = resp?.data?.fresh_analytics;

        if (freshAnalytics) {
          if (isSub) {
            await dispatch(updateSubAnalytics(resp?.data?.fresh_analytics));
          } else {
            await dispatch(updateAnalytics(resp?.data?.fresh_analytics));
          }
        }
      }

      await dispatch(setBuildingState({ createDocLoading: false }));
    } catch (e) {
      await dispatch(setBuildingState({ createDocLoading: false }));
    }
  };
